import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styles/styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';

import SVGIcon from 'components/SVGIcon/SVGIcon';
import { Viewport } from 'components/ViewportProvider';
import { Autoplay, Navigation } from 'swiper/modules';

import media from 'utils/mediaStyle';
import {
  AbcBakery,
  Anan,
  arrowNav,
  BelgoBelgian,
  Bellazza,
  BlancAndBlancs,
  BlankLounge,
  BodyImpact,
  customer1,
  customer2,
  customer3,
  customer4,
  customer5,
  customer6,
  customer7,
  DelisaFreshAlway,
  Downtown,
  ElGaucho,
  FamilyMart,
  FarmersMarket,
  FourPs,
  Go,
  GoldenGate,
  Gs25,
  JapaneseBBQ,
  Lariva,
  Lotte,
  Maguro,
  Manwah,
  Ministop,
  Muji,
  NhFoods,
  NoshLogo,
  OmakaseTigerLogo,
  OmEatery,
  OnYasai,
  Oryz,
  PizzaAndBia,
  quote,
  Rop,
  RyoSushi,
  SevenEleven,
  SharonArtisanBakery,
  Sheraton,
  SimplePlace,
  SolKitchen,
  Sukiya,
  Toki,
  TokyoDeli,
  TwoGuys,
  WhitePalace,
  Yan,
} from '../Images';
import { Container, SectionContainer, SectionTitle, SectionTitleBox } from './Partners';

const SwiperElementContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto 40px;
  overflow: visible;
  position: relative;
  background: #fafafa;
  ${media.md`
    padding-left: 16px;
    margin: 0 auto 80px;
    `}
  ${({ noPadding }) =>
    noPadding &&
    `
    padding: 0px !important;
  `}
  ${({ whiteBackground }) =>
    whiteBackground &&
    `
    background: #ffffff;
  `}
`;

const CustomerFeedbackSection = styled.div`
  max-width: 914px;
  margin: 0 auto;
  @media (max-width: 767px) {
    background: #f0f9f1;
    margin: 0 -16px;
    padding: 40px 16px;
  }
`;

const SwiperCustomerFeedbackContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  overflow: visible;
  position: relative;
`;

const SwiperSlideCustomerElement = styled(SwiperSlide)`
  position: relative;
  border-radius: 8px;
  display: flex !important;
  flex-direction: column;
  gap: 24px;
  cursor: pointer;
  ${media.lg`
    align-items: flex-start;
    flex-direction: row;
    gap: 64px;
    `}
`;

const SwiperSlideCustomerImageWrapper = styled.div`
  width: 100%;
  display: flex;
  border-radius: 16px;
  overflow: hidden;
  flex-shrink: 0;
  ${({ image }) => image && `background: url(${image});`}
  padding-top: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  ${media.lg`
    width: 320px;
    padding-top: 35%;
    `}
  ${({ subImage }) =>
    subImage &&
    `
      width: 64px;
      padding-top: 64px;
      border-radius: 8px;
    `}
    ${({ border }) =>
    border &&
    `
      border: 1px solid #CACBCE;
    `}
`;

const SwiperSlideQuoteImage = styled(SVGIcon)`
  & > div {
    justify-content: flex-start;
  }
`;

const SwiperSlideCustomerTextbox = styled.div`
  display: flex;
  flex-direction: column;
`;

const SwiperSlideCustomerFeedback = styled.p`
  margin: 16px 0 26px;
  font-size: 14px;
  line-height: 20px;
  font-style: normal;
  font-weight: 400;
  color: #000000;
  ${media.md`
      font-size: 16px;
      line-height: 24px;
    `}
`;

const SwiperSlideCustomerInfo = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SwiperSlideCustomerName = styled.p`
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #000000;
  ${media.md`
    font-size: 20px;
    line-height: 28px;
  `}
`;

const SwiperSlideCustomerSubInfo = styled.div`
  display: flex;
  align-items: center;
`;
const SwiperSlideCustomerSubInfoText = styled.div`
  font-size: 12px;
  font-style: normal;
  line-height: 16px;
  color: #808289;
  display: flex;
  align-items: center;
  &:not(:last-child)::after {
    content: '';
    background: #cacbce;
    width: 1px;
    height: 12px;
    margin: 0px 8px;
  }
  ${media.md`
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
  `}
  ${({ canClick }) =>
    canClick &&
    `
    &:hover {
      opacity: 0.6;
    }
  `}
`;

const SwiperSlideOverlay = styled.div`
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%
    background: #d4edd5;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 32px 40px;
    gap: 12px;
    border-radius: 8px;
    transition: opacity 0.3s ease;
    ${({ greenBackground }) =>
      greenBackground &&
      `
            background: #43A047
        `};
`;

const SwiperSlideElement = styled(SwiperSlide)`
  width: fit-content !important;
  position: relative;
  border-radius: 8px;
  &:hover > ${SwiperSlideOverlay} {
    opacity: 1;
  }
`;

const SwiperSlideBrandLogoWrapper = styled.div`
  width: 100%;
  height: 100%;
  max-width: 42px;
  max-height: 42px;
  display: flex;
  ${media.md`
    max-height: 62px;
    max-width: 62px;
    `}
`;

const SwiperSlideBrandLogoImage = styled.img`
  width: 100%;
  height: 100%;
`;

const NavigationButtonWrapper = styled.div`
  gap: 20px
  display: flex;
  align-items: center;
  width: fit-content;
  margin: 10px 0 0;
  ${media.lg`
    right: unset;
    gap: 24pxl;
    margin: 50px auto 0;
  `}
`;

const NavigationButton = styled.div`
  height: 44px;
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 80px;
  border: 2px solid #43a047;
  cursor: pointer;
  ${({ disabled }) =>
    disabled &&
    `
    border: 1px solid #b6e1b9;
  `}
  ${media.md`
    height: 56px;
    width: 56px;
  `}
  &:hover {
    background: #f0f9f1 !important;
  }
  &:active {
    background: #d4edd5 !important;
  }
`;

const NavigationButtonIcon = styled(SVGIcon)`
  ${({ disabled }) => disabled && `filter: opacity(0.5);`}
`;

const SlideNumber = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`;

const MarqueeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MarqueeWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  position: relative
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 320px;
    height: 100%;
    background: linear-gradient(270deg, rgba(255, 255, 255, 0) 66.09%, #fafafa 95%);
    z-index: 10;
    pointer-events: none;
  }
  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 320px;
    height: 100%;
    background: linear-gradient(270deg, #fafafa 5%, rgba(255, 255, 255, 0) 33.91%);
    z-index: 10;
    pointer-events: none;
  }
  @media (max-width: 767px) {
    &:before,
    &:after {
      display: none;
    }
  }
  &:last-child {
    width: 90%;
    margin: 0 auto;
  }
    ${({ whiteBackground }) =>
      whiteBackground &&
      `
        &:before {
          background: linear-gradient(270deg, rgba(255, 255, 255, 0) 66.09%, #ffffff 95%);
        }
        &:after {
          background: linear-gradient(270deg, #ffffff 5%, rgba(255, 255, 255, 0) 33.91%);
        }
    `}
`;

const MarqueeTrack = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  width: fit-content;
  animation: ${({ reverse }) =>
    `${reverse ? `scroll-right` : `scroll-left`}
    300s linear infinite;`}
  animation-play-state: running;
  @media(min-width: 578px) {
    ${MarqueeWrapper}:hover & {
      animation-play-state: paused;
    }
  }
  animation-play-state: ${({ isPaused }) => (isPaused ? 'paused' : 'running')};
  @keyframes scroll-left {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-87.5%);
    }
  }
  @keyframes scroll-right {
    0% {
      transform: translateX(-87.5%);
    }
    100% {
      transform: translateX(0);
    }
  }
  ${media.md`
    gap: 40px;
  `}
`;

const TrustedBySlider: FC<{ isPlay: boolean; whiteBackground?: boolean }> = ({ isPlay, whiteBackground }) => {
  const [isPaused, setIsPaused] = useState(true);

  const dataFirstLine = [
    Rop,
    BelgoBelgian,
    Sheraton,
    BlancAndBlancs,
    Oryz,
    Bellazza,
    Downtown,
    Sukiya,
    FourPs,
    Gs25,
    Ministop,
    Muji,
    FamilyMart,
    Lotte,
    Anan,
    FarmersMarket,
    PizzaAndBia,
    SimplePlace,
    TokyoDeli,
    SolKitchen,
    OmEatery,
  ];

  const dataSecondLine = [
    Lariva,
    RyoSushi,
    OnYasai,
    Maguro,
    DelisaFreshAlway,
    TwoGuys,
    SharonArtisanBakery,
    ElGaucho,
    Toki,
    NoshLogo,
    Yan,
    BodyImpact,
    JapaneseBBQ,
    OmakaseTigerLogo,
    BlankLounge,
    AbcBakery,
    SevenEleven,
    Go,
    NhFoods,
    WhitePalace,
    Manwah,
    GoldenGate,
  ];

  const repeatedData1 = Array.from({ length: 30 })
    .map(() => dataFirstLine)
    .reduce((acc, cur) => acc.concat(cur), []);
  const repeatedData2 = Array.from({ length: 28 })
    .map(() => dataSecondLine)
    .reduce((acc, cur) => acc.concat(cur), []);

  useEffect(() => {
    if (isPlay) {
      setIsPaused(false);
    }
  }, [isPlay]);
  return (
    <SwiperElementContainer whiteBackground={whiteBackground}>
      <MarqueeContainer
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <MarqueeWrapper whiteBackground={whiteBackground}>
          <MarqueeTrack reverse={true} isPaused={isPaused}>
            {repeatedData1.map((image, index) => (
              <SwiperSlideElement key={index}>
                <SwiperSlideBrandLogoWrapper>
                  <SwiperSlideBrandLogoImage src={image} />
                </SwiperSlideBrandLogoWrapper>
              </SwiperSlideElement>
            ))}
          </MarqueeTrack>
        </MarqueeWrapper>
        <MarqueeWrapper whiteBackground={whiteBackground}>
          <MarqueeTrack isPaused={isPaused}>
            {repeatedData2.map((image, index) => (
              <SwiperSlideElement key={index}>
                <SwiperSlideBrandLogoWrapper>
                  <SwiperSlideBrandLogoImage src={image} />
                </SwiperSlideBrandLogoWrapper>
              </SwiperSlideElement>
            ))}
          </MarqueeTrack>
        </MarqueeWrapper>
      </MarqueeContainer>
    </SwiperElementContainer>
  );
};

interface IProps {
  whiteBackground?: boolean;
}

const OurCustomers: FC<IProps> = ({ whiteBackground }) => {
  const lang = localStorage.getItem('lang') || 'vi';
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef: any = useRef();
  const trustedBrandsRef: any = useRef();
  const testimonialRef: any = useRef();
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [isAutoPlayTestimonial, setIsAutoPlayTestimonial] = useState(false);

  const loggedIn = !!localStorage.getItem('token');

  const customerFeedback = [
    {
      img: customer4,
      feedbackVi:
        'Kamereo có các chính sách hỗ trợ rất tốt dành cho doanh nghiệp, chúng tôi đánh giá cao chính sách công nợ và chính sách đổi trả. Ngoài ra Anan Saigon cũng gửi lời cảm ơn đến đội ngũ Kamereo đã hỗ trợ rất nhiệt tình trong những tình huống bất ngờ, giúp quy trình thu mua của chúng tôi luôn ổn định và nguyên liệu của cửa hàng luôn sẵn sàng để phục vụ thực khách',
      feedback:
        'Kamereo offers excellent support policies for businesses. We especially appreciate the credit terms and return policies. We’d also like to thank Kamereo’s team for their dedicated support during unexpected situations. Thanks to them, our procurement process remains stable and our kitchen is always stocked with fresh ingredients, ready to serve our guests.',
      customerName: '',
      position: lang === 'en' ? 'Supply Chain Department' : 'Bộ phận thu mua',
      companyName: 'Anan Saigon',
      border: true,
      link: 'https://anansaigon.com/',
    },
    {
      img: customer3,
      feedback:
        "What Gyu-Kaku values most about working with Kamereo is the clear invoicing, especially for vegetables and fruits, and the joint inspection policy upon delivery for added quality assurance. The produce is always fresh, meeting our restaurant's standards, ensuring transparent financial management and a stable supply chain to meet strict ingredient requirements.",
      feedbackVi:
        'Điều mà Gyu-Kaku thích nhất khi hợp tác với Kamereo là luôn có đầy đủ hóa đơn minh bạch, đặc biệt với mặt hàng rau củ quả. Thêm vào đó là chính sách đồng kiểm khi nhận hàng cũng giúp tôi yên tâm hơn về chất lượng, rau củ luôn tươi ngon và đúng yêu cầu của cửa hàng. Việc này không chỉ giúp tôi quản lý tài chính minh bạch mà còn duy trì được nguồn cung ổn định, đáp ứng các tiêu chuẩn nguyên liệu khắt khe của Gyu-Kaku.',
      customerName: '',
      position: lang === 'en' ? 'Supply Chain Department' : 'Bộ phận thu mua',
      companyName: 'Gyu-Kaku',
      link: 'https://gyu-kaku.com.vn/',
    },
    {
      img: customer5,
      feedbackVi:
        'Trước đây, việc duy trì nguồn cung rau củ tươi ngon cho nhà hàng luôn là một thử thách lớn. Chúng tôi phải làm việc với nhiều nhà cung cấp khác nhau. Kamereo đã giúp chúng tôi giải quyết vấn đề nguồn cung một cách hiệu quả, cung cấp rau củ quả tươi ngon, chất lượng ổn định và luôn giao hàng đúng hẹn.',
      feedback:
        'Maintaining a consistent supply of fresh vegetables used to be a major challenge for us. We had to work with multiple suppliers. Kamereo has effectively solved our supply issues by delivering fresh, high-quality produce with consistent standards and always on-time delivery.',
      customerName: '',
      position: lang === 'en' ? 'Chef' : 'Đầu bếp',
      companyName: 'Downtown Steakhouse',
      border: true,
      link: 'https://downtown.com.vn/',
    },
    {
      img: customer7,
      feedbackVi:
        'Mua hàng tại Kamereo rất tiện lợi, mình dễ dàng đặt qua app với hình ảnh sản phẩm và giá rõ ràng. Điều mình đặc biệt thích là Kamereo hỗ trợ giao hàng nhanh chóng, chỉ cần gửi đơn trước đêm hôm trước và sáng sớm là đã nhận được hàng, giúp bếp luôn sẵn sàng nguyên liệu để hoạt động. Từ khi hợp tác, mình chưa nhận được bất kỳ phàn nàn nào từ khách hàng về món ăn, điều này thực sự khiến mình rất yên tâm.',
      feedback:
        "Ordering from Kamereo is convenient with easy app orders, clear images, and listed prices. Their fast delivery, just ordering the night before for next-morning delivery, keeps our kitchen running smoothly. The vegetables and fruits are always fresh and well-kept. Since working with Kamereo, I haven't received any customer complaints, which gives me peace of mind.",
      customerName: '',
      position: lang === 'en' ? 'Restaurant Owner' : 'Chủ nhà hàng',
      companyName: 'RỐP - Cafe & Vegetarian',
      link: 'https://www.facebook.com/rop.vegetariancafe/',
    },
    {
      img: customer6,
      feedbackVi:
        'Hợp tác với Kamereo đã mang đến cho nông trại nguồn thu mua ổn định, giúp tôi yên tâm hơn trong công việc hàng ngày. Mỗi luống rau đều được chúng tôi chăm sóc tận tình, từ lúc gieo trồng cho đến khi thu hoạch, đảm bảo nông sản luôn tươi ngon, chất lượng nhất. Thật vui khi thấy những nỗ lực của mình được đền đáp, khi rau củ quả tươi ngon mà chúng tôi chăm sóc đều góp phần tạo nên những món ăn ngon miệng, dinh dưỡng.',
      feedback:
        "Partnering with Kamereo has brought a stable purchasing source to our farm, giving me peace of mind in daily operations. Each row of vegetables is carefully tended from sowing to harvesting to ensure freshness and quality. It's truly rewarding to see our hard work appreciated, knowing that the fresh produce we grow contributes to delicious and nutritious meals.",
      customerName: lang === 'en' ? 'Ms. Cẩm' : 'Cô Cẩm',
      position: lang === 'en' ? 'Farmer' : 'Nông dân',
      companyName: lang === 'en' ? 'Kamereo Partner Farm' : 'Nông trại hợp tác cùng Kamereo',
    },
    {
      img: customer1,
      feedbackVi:
        'Trước đây, nguồn thu mua của nông trại chúng tôi khá bấp bênh. Thỉnh thoảng tôi phải bán nông sản với giá thấp vì không tìm được đối tác, khiến công việc canh tác trở nên khó khăn. Từ khi hợp tác với Kamereo, chúng tôi đã có một nguồn thu mua ổn định và bền vững. Kamereo giúp chúng tôi duy trì đầu ra đều đặn, tạo động lực để tôi tiếp tục chăm sóc, canh tác rau củ quả tươi ngon.',
      feedback:
        'Previously, our farm’s sales were quite unstable. Sometimes I had to sell crops at low prices due to a lack of buyers, which made farming difficult. Since partnering with Kamereo, we’ve had a stable and sustainable purchasing channel. Kamereo helps us maintain a consistent output, which motivates me to continue cultivating high-quality, fresh vegetables.',
      customerName: lang === 'en' ? 'Ms. Hương' : 'Cô Hương',
      position: lang === 'en' ? 'Farmer' : 'Nông dân',
      companyName: lang === 'en' ? 'Kamereo Partner Farm' : 'Nông trại hợp tác cùng Kamereo',
    },
    {
      img: customer2,
      feedbackVi:
        'Tôi bắt đầu công việc vào lúc 6 giờ sáng, khi những thùng rau củ đã được anh em ca đêm trong kho kiểm tra cẩn thận và sẵn sàng giao. Tôi phải đảm bảo mỗi đơn rau củ khi giao không bị dập nát, đúng giờ và đúng địa điểm. Điều làm tôi vui nhất là khách hàng luôn hài lòng với chất lượng rau củ. Đó là động lực để tôi tiếp tục công việc dù nắng hay mưa, đưa nông sản đến tay doanh nghiệp trọn vẹn và đáng tin cậy.',
      feedback:
        'With Kamereo, we feel completely assured and satisfied. Thanks to the high-quality supply sources, we can more easily control our ingredients as well as our business operations. Additionally, The Tangerine would like to thank the team for their enthusiastic support in handling unexpected situations.',
      customerName: lang === 'en' ? 'Mr. Kỳ Anh' : 'Anh Kỳ Anh',
      position: '',
      companyName: lang === 'en' ? 'Delivery staff at Kamereo' : 'Nhân viên giao hàng tại Kamereo',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isIntersecting) {
          setTimeout(() => {
            setIsIntersecting(true);
          }, 3000);
        }
      },
      {
        threshold: 1,
      },
    );
    const customersObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isAutoPlayTestimonial) {
          setIsAutoPlayTestimonial(true);
          swiperRef.current.autoplay.start();
        }
      },
      {
        threshold: 0.8,
      },
    );
    if (trustedBrandsRef.current) {
      observer.observe(trustedBrandsRef.current);
    }
    if (testimonialRef.current) {
      customersObserver.observe(testimonialRef.current);
    }

    return () => {
      if (trustedBrandsRef.current) {
        observer.unobserve(trustedBrandsRef.current);
      }
      if (testimonialRef.current) {
        customersObserver.unobserve(testimonialRef.current);
      }
    };
  }, []);
  return (
    <Viewport.Consumer>
      {({ width }) => {
        const isNotMobile = width > 767;
        return (
          <SectionContainer removePaddingBottom={!isNotMobile} gray={!whiteBackground}>
            <Container loggedIn={loggedIn}>
              <SectionTitleBox>
                {lang === 'vi' ? (
                  <>
                    <SectionTitle>Được tin tưởng bởi</SectionTitle>
                    <SectionTitle highLight>&nbsp;hơn 4.000 doanh nghiệp F&B</SectionTitle>
                  </>
                ) : (
                  <>
                    <SectionTitle>
                      Trusted by
                      <SectionTitle highLight>&nbsp;4,000+ F&B Businesses</SectionTitle>
                    </SectionTitle>
                  </>
                )}
              </SectionTitleBox>
              <div ref={trustedBrandsRef}>
                <TrustedBySlider isPlay={isIntersecting} whiteBackground={whiteBackground} />
              </div>
              <CustomerFeedbackSection>
                <SectionTitleBox>
                  {lang === 'vi' ? (
                    <>
                      <SectionTitle>Hành trình tươi ngon&nbsp;</SectionTitle>
                      <SectionTitle highLight>được tạo nên bởi những con người thật</SectionTitle>
                    </>
                  ) : (
                    <>
                      <SectionTitle>
                        A fresh journey
                        <SectionTitle highLight>&nbsp;crafted by real people</SectionTitle>
                      </SectionTitle>
                    </>
                  )}
                </SectionTitleBox>
                <SwiperCustomerFeedbackContainer ref={testimonialRef}>
                  <Swiper
                    onSwiper={(swiper) => {
                      swiperRef.current = swiper;
                    }}
                    slidesPerView={1}
                    spaceBetween={20}
                    modules={[Navigation, Autoplay]}
                    onSlideChange={(swiper) => {
                      setActiveIndex(swiper.realIndex);
                    }}
                    loop={true}
                    autoplay={
                      isNotMobile && isAutoPlayTestimonial
                        ? {
                            delay: 5000,
                            disableOnInteraction: false,
                            pauseOnMouseEnter: true,
                          }
                        : false
                    }
                  >
                    {customerFeedback.map((item, index) => (
                      <SwiperSlideCustomerElement key={index}>
                        {isNotMobile && <SwiperSlideCustomerImageWrapper image={item.img} border={item.border} />}
                        <SwiperSlideCustomerTextbox>
                          <SwiperSlideQuoteImage src={quote} />
                          <SwiperSlideCustomerFeedback>
                            {lang === 'vi' ? item.feedbackVi : item.feedback}
                          </SwiperSlideCustomerFeedback>
                          <SwiperSlideCustomerInfo>
                            {!isNotMobile && <SwiperSlideCustomerImageWrapper subImage image={item.img} />}
                            <SwiperSlideCustomerTextbox>
                              <SwiperSlideCustomerName>{item.customerName}</SwiperSlideCustomerName>
                              <SwiperSlideCustomerSubInfo>
                                {item.position && (
                                  <SwiperSlideCustomerSubInfoText>{item.position}</SwiperSlideCustomerSubInfoText>
                                )}
                                <SwiperSlideCustomerSubInfoText
                                  canClick={item.link}
                                  onClick={() => item.link && window.open(item.link, '_blank')}
                                >
                                  {item.companyName}
                                </SwiperSlideCustomerSubInfoText>
                              </SwiperSlideCustomerSubInfo>
                            </SwiperSlideCustomerTextbox>
                          </SwiperSlideCustomerInfo>
                        </SwiperSlideCustomerTextbox>
                      </SwiperSlideCustomerElement>
                    ))}
                    <NavigationButtonWrapper>
                      <NavigationButton
                        onClick={() => {
                          swiperRef.current.slidePrev();
                        }}
                      >
                        <NavigationButtonIcon src={arrowNav} style={{ transform: 'rotate(180deg)' }} />
                      </NavigationButton>
                      <SlideNumber>{`${activeIndex + 1}/${customerFeedback.length}`}</SlideNumber>
                      <NavigationButton
                        onClick={() => {
                          swiperRef.current.slideNext();
                        }}
                      >
                        <NavigationButtonIcon src={arrowNav} />
                      </NavigationButton>
                    </NavigationButtonWrapper>
                  </Swiper>
                </SwiperCustomerFeedbackContainer>
              </CustomerFeedbackSection>
            </Container>
          </SectionContainer>
        );
      }}
    </Viewport.Consumer>
  );
};

export default OurCustomers;
